// src/pages/RegistroCorrispondenzaPage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { esportaXLSX } from "../utils/esportaXLSX";
import { Search, ChevronDown, ChevronUp, ExternalLink, Download, X } from "lucide-react";

const ICONE_TIPO = { PDF:"📄", DOC:"📝", DOCX:"📝", XLS:"📊", XLSX:"📊", EML:"📧", TXT:"📃" };
const RAGGRUPPAMENTI = [
  { id: "commessa", label: "Per commessa" },
  { id: "cronologico", label: "Cronologico" },
  { id: "mese", label: "Per mese" },
  { id: "anno", label: "Per anno" },
];

function chiaveGruppo(d, rag) {
  if (rag === "commessa") return d.codiceCommessa ? `[${d.codiceCommessa}] ${d.projectName||""}` : (d.projectName||"Senza commessa");
  if (rag === "mese") {
    if (!d.dataComunicazione) return "Data non specificata";
    const [y, m] = d.dataComunicazione.split("-");
    const nomi = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
    return `${nomi[parseInt(m)-1]} ${y}`;
  }
  if (rag === "anno") return d.dataComunicazione ? d.dataComunicazione.split("-")[0] : "Anno non specificato";
  return d.dataComunicazione ? new Date(d.dataComunicazione+"T00:00:00").toLocaleDateString("it-IT",{weekday:"long",year:"numeric",month:"long",day:"numeric"}) : "Data non specificata";
}

export default function RegistroCorrispondenzaPage() {
  const { effectiveOrgId } = useOrg();
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [tutti, setTutti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cerca, setCerca] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [soloEnti, setSoloEnti] = useState(false);
  const CATEGORIE_ENTI = ["Ente","Ag. Dogane","e-distribuzione"];
  const [raggruppamento, setRaggruppamento] = useState("commessa");
  const [aperte, setAperte] = useState({});
  const [selectedCom, setSelectedCom] = useState(null);

  useEffect(() => {
    if (!effectiveOrgId) return;
    setLoading(true);
    return onSnapshot(
      query(collection(db, "comunicazioni"), where("orgId", "==", effectiveOrgId)),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.dataComunicazione||"").localeCompare(a.dataComunicazione||""));
        setTutti(list);
        setLoading(false);
        if (selectedCom) {
          const ag = list.find(x => x.id === selectedCom.id);
          if (ag) setSelectedCom(ag);
        }
      },
      err => { console.error(err); setLoading(false); }
    );
  }, [effectiveOrgId]);

  const filtrati = tutti.filter(d => {
    if (soloEnti && !CATEGORIE_ENTI.includes(d.categoria)) return false;
    if (filtroCategoria && d.categoria !== filtroCategoria) return false;
    if (cerca) {
      const s = cerca.toLowerCase();
      return [d.numero, d.oggetto, d.mittente, d.destinatario, d.categoria, d.projectName, d.nomeFile]
        .some(v => (v||"").toLowerCase().includes(s));
    }
    return true;
  });

  const gruppiMap = filtrati.reduce((acc, d) => {
    const k = chiaveGruppo(d, raggruppamento);
    if (!acc[k]) acc[k] = [];
    acc[k].push(d);
    return acc;
  }, {});

  const gruppiOrdinati = Object.entries(gruppiMap).sort(([ka, va], [kb, vb]) => {
    if (raggruppamento === "mese" || raggruppamento === "anno" || raggruppamento === "cronologico") {
      return (vb[0]?.dataComunicazione||"").localeCompare(va[0]?.dataComunicazione||"");
    }
    return ka.localeCompare(kb);
  });

  const toggleGruppo = k => setAperte(a => ({ ...a, [k]: a[k] === false ? true : false }));
  const isAperto = k => aperte[k] !== false;

  const esporta = () => {
    const intestazione = ["ID","Data","Commessa","Mittente","Destinatario","Oggetto","Categoria","Tipo","File"];
    const righe = filtrati.map(d => [
      d.numero||"—", d.dataComunicazione||"—",
      d.codiceCommessa ? `[${d.codiceCommessa}] ${d.projectName||""}` : (d.projectName||"—"),
      d.mittente||"—", d.destinatario||"—", d.oggetto||"—",
      d.categoria||"—", d.tipoDocumento||"—", d.nomeFile||"—"
    ]);
    esportaXLSX([intestazione,...righe],
      `Registro_Corrispondenza_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,
      "Corrispondenza");
  };

  const inp = { padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:7,
    fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none" };

  // Vista dettaglio
  if (selectedCom) {
    const tipo = ICONE_TIPO[selectedCom.tipoDocumento] || "📄";
    return (
      <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid #e5e7eb", flexShrink:0,
          background:"#faf5ff", display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontFamily:"monospace", fontWeight:800, color:"#7c3aed", fontSize:14, marginBottom:4 }}>
              {selectedCom.numero}
            </div>
            <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{selectedCom.oggetto}</div>
            <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>
              {selectedCom.categoria} · {selectedCom.mittente}
              {selectedCom.dataComunicazione && ` · ${new Date(selectedCom.dataComunicazione+"T00:00:00").toLocaleDateString("it-IT")}`}
            </div>
            {selectedCom.projectName && (
              <div style={{ fontSize:11, color:"#7c3aed", fontWeight:600, marginTop:2 }}>
                {selectedCom.codiceCommessa ? `[${selectedCom.codiceCommessa}] ` : ""}{selectedCom.projectName}
              </div>
            )}
          </div>
          <button onClick={() => setSelectedCom(null)}
            style={{ padding:"5px 10px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", cursor:"pointer", flexShrink:0 }}>
            <X size={13}/>
          </button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:16 }}>
          <div style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:12, marginBottom:12 }}>
            {[
              ["Mittente", selectedCom.mittente||"—"],
              ["Destinatario", selectedCom.destinatario||"—"],
              ["Categoria", selectedCom.categoria||"—"],
              ["Tipo documento", selectedCom.tipoDocumento ? `${tipo} ${selectedCom.tipoDocumento}` : "—"],
              ["Data comunicazione", selectedCom.dataComunicazione ? new Date(selectedCom.dataComunicazione+"T00:00:00").toLocaleDateString("it-IT") : "—"],
              ["Commessa", selectedCom.codiceCommessa ? `[${selectedCom.codiceCommessa}] ${selectedCom.projectName||""}` : (selectedCom.projectName||"—")],
              ["Registrata da", selectedCom.createdBy||"—"],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", gap:8, marginBottom:6, fontSize:12 }}>
                <span style={{ color:"#6b7280", fontWeight:600, minWidth:140, flexShrink:0 }}>{k}</span>
                <span style={{ color:"#111827" }}>{v}</span>
              </div>
            ))}
            {selectedCom.note && (
              <div style={{ marginTop:8, padding:8, background:"#f9fafb", borderRadius:6, fontSize:12, color:"#374151" }}>
                {selectedCom.note}
              </div>
            )}
          </div>
          {selectedCom.fileUrl && (
            <div style={{ border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden" }}>
              <div style={{ background:"#fffbeb", padding:"8px 12px", borderBottom:"1px solid #fde68a",
                display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:12, fontWeight:700, color:"#d97706" }}>{tipo} {selectedCom.nomeFile}</span>
                <div style={{ display:"flex", gap:8 }}>
                  <a href={selectedCom.fileUrl} target="_blank" rel="noreferrer"
                    style={{ fontSize:11, color:"#7c3aed", textDecoration:"none", fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
                    <ExternalLink size={12}/> Apri
                  </a>
                  <a href={selectedCom.fileUrl} download={selectedCom.nomeFile}
                    style={{ fontSize:11, color:"#059669", textDecoration:"none", fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
                    <Download size={12}/> Scarica
                  </a>
                </div>
              </div>
              {selectedCom.tipoDocumento === "PDF" && (
                <iframe src={selectedCom.fileUrl} style={{ width:"100%", height:300, border:"none" }} title={selectedCom.nomeFile}/>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"16px 24px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
          <h2 style={{ fontSize:18, fontWeight:800, color:"#1e1b4b", margin:0 }}>📨 Registro Corrispondenza</h2>
          <span style={{ fontSize:11, color:"#9ca3af" }}>{filtrati.length} doc.</span>
          <button onClick={esporta}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px",
              border:"1px solid #059669", borderRadius:7, background:"#f0fdf4",
              color:"#059669", fontSize:12, fontWeight:700, cursor:"pointer" }}>
            📊 Excel
          </button>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ position:"relative" }}>
            <Search size={12} style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", color:"#9ca3af" }}/>
            <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca..."
              style={{ ...inp, paddingLeft:26, width:240 }}/>
          </div>
          <button onClick={() => setSoloEnti(v=>!v)}
            style={{padding:"6px 12px",border:"1px solid "+(soloEnti?"#0369a1":"#e5e7eb"),borderRadius:8,
              background:soloEnti?"#e0f2fe":"#fff",color:soloEnti?"#0369a1":"#374151",
              fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}
            title="Ente, Agenzia Dogane, e-distribuzione">
            🏛️ Pratiche con enti
          </button>
          <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} style={inp}>
            <option value="">Tutte le categorie</option>
            {[...new Set(tutti.map(d => d.categoria).filter(Boolean))].sort().map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select value={raggruppamento} onChange={e => setRaggruppamento(e.target.value)}
            style={{ ...inp, fontWeight:600, color:"#7c3aed", borderColor:"#c4b5fd" }}>
            {RAGGRUPPAMENTI.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          {(cerca || filtroCategoria || soloEnti) && (
            <button onClick={() => { setCerca(""); setFiltroCategoria(""); setSoloEnti(false); }}
              style={{ ...inp, cursor:"pointer", background:"#fff" }}>× Reset</button>
          )}
        </div>
      </div>

      <div style={{ flex:1, overflow:"auto", padding:"16px 24px" }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>Caricamento...</div>
        ) : gruppiOrdinati.length === 0 ? (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
            <div style={{ fontSize:48, opacity:.1, marginBottom:12 }}>📨</div>
            <div style={{ fontSize:14, fontWeight:600 }}>Nessuna corrispondenza</div>
          </div>
        ) : gruppiOrdinati.map(([gruppo, docs]) => {
          const aperto = isAperto(gruppo);
          return (
            <div key={gruppo} style={{ marginBottom:10 }}>
              <div onClick={() => toggleGruppo(gruppo)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
                  background:"#f5f3ff", border:"1px solid #e5e7eb",
                  borderRadius: aperto ? "8px 8px 0 0" : 8, cursor:"pointer", userSelect:"none" }}>
                {aperto ? <ChevronDown size={14} color="#7c3aed"/> : <ChevronUp size={14} color="#7c3aed"/>}
                <span style={{ fontSize:13, fontWeight:700, color:"#1e1b4b", flex:1 }}>{gruppo}</span>
                <span style={{ fontSize:11, padding:"1px 8px", borderRadius:10,
                  background:"#ede9fe", color:"#7c3aed", fontWeight:700 }}>{docs.length} doc.</span>
              </div>
              {aperto && (
                <div style={{ border:"1px solid #e5e7eb", borderTop:"none", borderRadius:"0 0 8px 8px", overflow:"hidden" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr style={{ background:"#faf9ff", borderBottom:"1px solid #e5e7eb" }}>
                        {["ID","Data","Mittente","Oggetto","Categoria","Tipo",""].map(h => (
                          <th key={h} style={{ padding:"7px 12px", textAlign:"left", fontSize:10,
                            fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".04em" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map((d, i) => (
                        <tr key={d.id} onClick={() => setSelectedCom(d)}
                          style={{ borderBottom: i < docs.length-1 ? "1px solid #f3f4f6" : "none",
                            background: i%2===0 ? "#fff" : "#fafafa", cursor:"pointer" }}
                          onMouseEnter={e => e.currentTarget.style.background="#f5f3ff"}
                          onMouseLeave={e => e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                          <td style={{ padding:"9px 12px" }}>
                            <span style={{ fontFamily:"monospace", fontWeight:700, fontSize:12, color:"#7c3aed" }}>
                              {d.numero||"—"}
                            </span>
                          </td>
                          <td style={{ padding:"9px 12px", fontSize:11, color:"#6b7280", whiteSpace:"nowrap" }}>
                            {d.dataComunicazione ? new Date(d.dataComunicazione+"T00:00:00").toLocaleDateString("it-IT") : "—"}
                          </td>
                          <td style={{ padding:"9px 12px", fontSize:12, color:"#374151" }}>{d.mittente||"—"}</td>
                          <td style={{ padding:"9px 12px", maxWidth:260 }}>
                            <div style={{ fontSize:12, fontWeight:500, color:"#111827",
                              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {d.oggetto||"—"}
                            </div>
                            {raggruppamento !== "commessa" && d.projectName && (
                              <div style={{ fontSize:10, color:"#9ca3af", marginTop:1 }}>
                                {d.codiceCommessa ? `[${d.codiceCommessa}] ` : ""}{d.projectName}
                              </div>
                            )}
                          </td>
                          <td style={{ padding:"9px 12px" }}>
                            <span style={{ fontSize:10, padding:"1px 7px", borderRadius:8,
                              background:"#f3f4f6", color:"#6b7280", fontWeight:600 }}>
                              {d.categoria||"—"}
                            </span>
                          </td>
                          <td style={{ padding:"9px 12px", fontSize:11, color:"#6b7280" }}>
                            {d.tipoDocumento ? `${ICONE_TIPO[d.tipoDocumento]||"📄"} ${d.tipoDocumento}` : "—"}
                          </td>
                          <td style={{ padding:"9px 12px" }}>
                            {d.fileUrl && (
                              <a href={d.fileUrl} target="_blank" rel="noreferrer"
                                onClick={e => e.stopPropagation()}
                                style={{ color:"#7c3aed", display:"flex", alignItems:"center" }}>
                                <ExternalLink size={13}/>
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
